// Маршрут до конкретного сообщения и канон deep-link (SPEC §4.4; R2 п.L).
//
// КАНОН (правка L, CONTRACTS.md): `/chat/<chatId>?msg=<messageId>` — это MESSAGE-id, НЕ seq
// (правка L сняла конфликт notifications-push↔app-ui — генератор и потребитель сходятся на messageId,
// shared-contracts/push.ts.deepLink). app-ui — потребитель формы; разбор/сборка тут ПУБЛИКУЮТ её как
// ожидаемую (страж-тест round-trip), а pwa-shell (parseDeepLink) даёт ту же форму. Парсер устойчив к
// битым/незнакомым ссылкам → деградация на `/chat/<chatId>` или `/` без падения (SPEC §4.4).

import type { ChatId, MessageId } from '@tele-chat/shared-contracts'

/** Цель навигации по deep-link: чат + (опц.) конкретное сообщение для доскролла. */
export interface DeepLinkTarget {
  chatId: ChatId
  /** ОТСУТСТВУЕТ, если ссылка ведёт в чат без якоря сообщения. */
  messageId?: MessageId
}

/** Имя query-параметра якоря сообщения в маршруте чата (канон L). */
export const MSG_PARAM = 'msg' as const

/** Путь экрана чата без якоря. */
export function chatPath(chatId: ChatId): string {
  return `/chat/${encodeURIComponent(chatId)}`
}

/**
 * Собирает канонический deep-link `/chat/<chatId>?msg=<messageId>` (правка L).
 * Та же форма, что pwa-shell.buildDeepLink и push-payload.deepLink — совпадение публикуется как
 * ожидаемое (страж round-trip; при расхождении с notifications-push — сверка через R2).
 */
export function buildMessageDeepLink(chatId: ChatId, messageId: MessageId): string {
  return `${chatPath(chatId)}?${MSG_PARAM}=${encodeURIComponent(messageId)}`
}

/**
 * Разбирает deep-link (путь `/chat/<id>?msg=<id>` или абсолютный URL под нашим origin) в цель.
 * Возвращает `null`, если путь не наш (не `/chat/<chatId>`) — вызывающий уходит в дженерик-деградацию
 * (на корень `/`), а не молча навигирует «в никуда» (SPEC §4.4).
 */
export function parseMessageDeepLink(url: string): DeepLinkTarget | null {
  let parsed: URL
  try {
    // База нужна только для разбора относительного пути; origin не используется.
    parsed = new URL(url, 'https://app.local')
  } catch {
    return null
  }
  const segments = parsed.pathname.split('/').filter((s) => s.length > 0)
  if (segments.length < 2 || segments[0] !== 'chat') return null

  const rawChat = segments[1]
  if (rawChat === undefined) return null
  const chatId = decodeURIComponent(rawChat) as ChatId
  if (chatId.length === 0) return null

  const rawMsg = parsed.searchParams.get(MSG_PARAM)
  const messageId =
    rawMsg !== null && rawMsg.length > 0 ? (decodeURIComponent(rawMsg) as MessageId) : undefined

  return messageId !== undefined ? { chatId, messageId } : { chatId }
}

/** true, если deep-link ведёт в КОНКРЕТНОЕ сообщение (а не «просто открыть чат»). */
export function pointsToMessage(url: string): boolean {
  const target = parseMessageDeepLink(url)
  return target !== null && target.messageId !== undefined
}
